import AppContext from '../AppContext';

const handleResponse = (response) => {
    return response.json()
        .then((data) => {
            if (!data.success) {
                throw new Error(data.message);
            }

            return data.data;
        });
};

const handleRes = (response) => {
    return response.json()
        .then((data) => {
            if (!data.success) {
                throw ({ message: data.message, errors: data.data });
            }

            return data.data;
        });
};

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${AppContext.getAuthToken()}`
});

const APIService = {
    init() {

    },

    // API -- Auth
    doLogin(username, password) {
        return fetch('/api/auth/login', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                username,
                password
            })
        }).then(handleResponse);
    },

    doLogout() {
        return fetch('/api/auth/logout', {
            method: 'POST',
            headers: getHeaders()
        }).then(handleResponse);
    },

    getUser() {
        return fetch('/api/auth/user', {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    updateUser(user) {
        return fetch('/api/auth/user', {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(user)
        }).then(handleResponse);
    },

    getStatistics(id) {
        return fetch(`/api/statistics/${id}`, {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    // API -- Restaurants
    getRestaurants() {
        return fetch('/api/restaurants', {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    getRestaurant(id) {
        return fetch(`/api/restaurants/${id}`, {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    storeRestaurant(restaurant) {
        return fetch('/api/restaurants', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(restaurant)
        }).then(handleResponse);
    },

    updateRestaurant(id, restaurant) {
        return fetch(`/api/restaurants/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(restaurant)
        }).then(handleResponse);
    },

    destroyRestaurant(id) {
        return fetch(`/api/restaurants/${id}`, {
            method: 'DELETE',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    // API -- Organisations
    getOrganisations() {
        return fetch('/api/organisations', {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    getOrganisation(id) {
        return fetch(`/api/organisations/${id}`, {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    storeOrganisation(organisation) {
        return fetch('/api/organisations', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(organisation)
        }).then(handleResponse);
    },

    updateOrganisation(id, organisation) {
        return fetch(`/api/organisations/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(organisation)
        }).then(handleResponse);
    },

    destroyOrganisation(id) {
        return fetch(`/api/organisations/${id}`, {
            method: 'DELETE',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    // API -- Menus
    getMenus(restaurantId, date = '', type = '') {
        return fetch(`/api/restaurants/${restaurantId}/menus?date=${date}&type=${type}`, {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    getPaginatedMenus(restaurantId, data) {
        return fetch(`/api/restaurants/${restaurantId}/menus/paginated`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ data })
        }).then(handleResponse);
    },

    getMenu(restaurantId, menuId) {
        return fetch(`/api/restaurants/${restaurantId}/menus/${menuId}`, {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    storeMenu(restaurantId, menu) {
        return fetch(`/api/restaurants/${restaurantId}/menus`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(menu)
        }).then(handleResponse);
    },

    updateMenu(restaurantId, menuId, menu) {
        return fetch(`/api/restaurants/${restaurantId}/menus/${menuId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(menu)
        }).then(handleResponse);
    },

    destroyMenu(restaurantId, menuId) {
        return fetch(`/api/restaurants/${restaurantId}/menus/${menuId}`, {
            method: 'DELETE',
            headers: getHeaders()
        }).then(handleResponse);
    },

    destroyMenuBetween(restaurantId, from, to) {
        return fetch(`/api/restaurants/${restaurantId}/menus`, {
            method: 'DELETE',
            headers: getHeaders(),
            body: JSON.stringify({
                from, to
            })
        }).then(handleResponse);
    },

    storeMenuItem(restaurantId, menuId, item) {
        return APIService.storeMenuItems(restaurantId, menuId, [item]);
    },

    storeMenuItems(restaurantId, menuId, items) {
        return fetch(`/api/restaurants/${restaurantId}/menus/${menuId}/items`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(items)
        }).then(handleResponse);
    },

    destroyMenuItem(restaurantId, menuId, itemId) {
        return fetch(`/api/restaurants/${restaurantId}/menus/${menuId}/items/${itemId}`, {
            method: 'DELETE',
            headers: getHeaders()
        }).then(handleResponse);
    },

    // API -- Reports
    getReports(restaurantId, settings) {
        return fetch(`/api/restaurants/${restaurantId}/getReports`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ settings })
        }).then(handleResponse);
    },

    getOrganisationReports(organisationId, settings) {
        return fetch(`/api/organisations/${organisationId}/reports`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ settings })
        }).then(handleResponse);
    },

    getAllReports(settings) {
        return fetch('/api/reports/all', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ settings })
        }).then(handleResponse);
    },

    getReport(restaurantId, reportId) {
        return fetch(`/api/restaurants/${restaurantId}/reports/${reportId}`, {
            method: 'GET',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    storeReport(restaurantId, date) {
        return fetch(`/api/restaurants/${restaurantId}/reports`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ date })
        }).then(handleResponse);
    },

    updateReport(restaurantId, reportId, report) {
        return fetch(`/api/restaurants/${restaurantId}/reports/${reportId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(report)
        }).then(handleResponse);
    },

    destroyReport(restaurantId, reportId) {
        return fetch(`/api/restaurants/${restaurantId}/reports/${reportId}`, {
            method: 'DELETE',
            headers: getHeaders(),

        });
    },
    getFoodTypes(getDeleted = false) {
        return fetch('/api/foodtypes' + (getDeleted ? '?deleted=1' : ''), {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    storeFoodType(type) {
        return fetch('/api/foodtypes', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(type)
        }).then(handleResponse);
    },

    destroyFoodType(typeId) {
        return fetch(`/api/foodtypes/${typeId}`, {
            method: 'DELETE',
            headers: getHeaders()
        }).then(handleResponse);
    },

    getFoodServiceCategories() {
        return fetch('/api/food-service-categories', {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    storeFoodServiceCategory(data) {
        return fetch('/api/food-service-categories', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        }).then(handleRes);
    },

    deleteFoodServiceCategory(serviceId) {
        return fetch(`/api/food-service-categories/${serviceId}`, {
            method: 'DELETE',
            headers: getHeaders(),
        }).then(handleResponse);
    },

    export(restaurants, foodServiceCategories, municipalities, regions, from, to, locale) {
        return fetch('/api/export', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                restaurants,
                foodServiceCategories,
                municipalities,
                regions,
                from,
                to,
                locale
            })
        }).then(handleResponse);
    },

    getExports() {
        return fetch('/api/export/all', {
            method: 'GET',
            headers: getHeaders()
        }).then(handleResponse);
    },

    getFeedbackQuestions(data) {
        return fetch('/api/f/questions', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        }).then(handleResponse);
    },

    updateFeedbackQuestions(data) {
        return fetch('/api/f/questions/update', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        }).then(handleRes);
    },

    deleteFeedbackQuestions(data) {
        return fetch('/api/f/questions/delete', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        }).then(handleRes);
    },

    exportFeedbackReport(restaurants, from, to, locale) {
        return fetch('/api/f/export', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ restaurants, from, to, locale })
        }).then(async (res) => {
            if (res.status !== 200) {
                const resJson = await res.json();
                throw ({ message: resJson.message, errors: resJson.data });
            }
            const url = window.URL.createObjectURL(new Blob([await res.blob()]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'feedback.xlsx');
            document.body.appendChild(link);
            link.click();
        });
    }
};

export default APIService;



// WEBPACK FOOTER //
// ./resources/assets/js/services/APIService.js